import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Grid, MenuItem, TextField } from "@mui/material";
import Modal from "@mui/material/Modal";
import {
  CLASSIFICATION_ALL,
  resolveServiceGroupForRole,
  filterClassificationsForGroup,
} from "../api/diagnosticDoctorApi";
import {
  DIAGNOSTIC_USER_TYPES,
  normalizeDiagnosticUserType,
  normalizeSalutation,
  salutationSelectOptions,
} from "../utils/diagnosticUserFormShared";
import { modalBoxSx } from "./bedMasterStyles";

const GENDERS = [
  { value: "M", label: "Male" },
  { value: "F", label: "Female" },
  { value: "T", label: "Transgender" },
];

const classificationSelectValue = (id) =>
  id == null || id === "" ? CLASSIFICATION_ALL : String(id);

export default function DiagnosticUserEditModal({
  open,
  user,
  lookups = { groups: [], classifications: [] },
  onClose,
  onSave,
  isSaving = false,
}) {
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!user) {
      setForm(null);
      return;
    }
    setForm({
      username: user.username || "",
      user_type: normalizeDiagnosticUserType(
        user.user_type || user.role || user.role_type || "Radiologist",
      ),
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      email: user.email || "",
      phone_no: user.phone_no || "",
      password: "",
      registration_no: user.registration_no || "",
      specialization: user.specialization || "",
      qualifications: user.qualifications || "",
      salutation: normalizeSalutation(user.salutation || ""),
      gender: user.gender || "",
      hasPassword: Boolean(user.username),
      service_group_id: user.service_group_id
        ? String(user.service_group_id)
        : "",
      service_classification_id: classificationSelectValue(
        user.service_classification_id,
      ),
      active: user.active !== false && user.active !== "Inactive",
    });
  }, [user]);

  const groupClassifications = useMemo(() => {
    if (!form?.service_group_id) return [];
    return filterClassificationsForGroup(
      lookups.classifications,
      Number(form.service_group_id),
    );
  }, [form?.service_group_id, lookups.classifications]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleUserTypeChange = (nextType) => {
    const roleType = String(nextType || "").trim().toLowerCase();
    const matched = resolveServiceGroupForRole(roleType, lookups.groups);
    setForm((prev) => ({
      ...prev,
      user_type: nextType,
      service_group_id: matched?.id ? String(matched.id) : "",
      service_classification_id: CLASSIFICATION_ALL,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  if (!form) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      style={{
        backdropFilter: "blur(5px)",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
      }}
    >
      <Box sx={{ ...modalBoxSx, maxHeight: "90vh", overflowY: "auto", width: "560px" }}>
        <h2 style={{ marginTop: 0 }}>Edit Diagnostic User</h2>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Username"
                value={form.username}
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                required
                label="User Type"
                value={form.user_type}
                onChange={(e) => handleUserTypeChange(e.target.value)}
                fullWidth
              >
                {DIAGNOSTIC_USER_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Salutation"
                value={form.salutation}
                onChange={(e) => updateField("salutation", e.target.value)}
                fullWidth
              >
                {salutationSelectOptions(form.salutation).map((title) => (
                  <MenuItem key={title} value={title}>
                    {title}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                label="First Name"
                value={form.first_name}
                onChange={(e) => updateField("first_name", e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                label="Last Name"
                value={form.last_name}
                onChange={(e) => updateField("last_name", e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                label="Email"
                type="email"
                value={form.email}
                onChange={(e) => updateField("email", e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone No"
                value={form.phone_no}
                onChange={(e) => updateField("phone_no", e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Password"
                type="password"
                value={form.password}
                onChange={(e) => updateField("password", e.target.value)}
                placeholder={
                  form.hasPassword
                    ? "Leave blank to keep current password"
                    : "Enter new password"
                }
                helperText={
                  form.hasPassword
                    ? "Password is already set for this account."
                    : ""
                }
                autoComplete="new-password"
                inputProps={{
                  autoComplete: "new-password",
                  "data-lpignore": "true",
                  "data-1p-ignore": "true",
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Registration No"
                value={form.registration_no}
                onChange={(e) => updateField("registration_no", e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Specialization"
                value={form.specialization}
                onChange={(e) => updateField("specialization", e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Qualifications"
                value={form.qualifications}
                onChange={(e) => updateField("qualifications", e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                required
                label="Service Group"
                value={form.service_group_id}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    service_group_id: e.target.value,
                    service_classification_id: CLASSIFICATION_ALL,
                  }))
                }
                fullWidth
              >
                <MenuItem value="">Select Service Group</MenuItem>
                {lookups.groups.map((group) => (
                  <MenuItem key={group.id} value={String(group.id)}>
                    {group.name} ({group.code})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Service Classification"
                value={form.service_classification_id}
                onChange={(e) =>
                  updateField("service_classification_id", e.target.value)
                }
                fullWidth
                disabled={!form.service_group_id}
              >
                <MenuItem value={CLASSIFICATION_ALL}>All</MenuItem>
                {groupClassifications.map((item) => (
                  <MenuItem key={item.id} value={String(item.id)}>
                    {item.name} ({item.code})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                required
                label="Gender"
                value={form.gender}
                onChange={(e) => updateField("gender", e.target.value)}
                fullWidth
              >
                {GENDERS.map((g) => (
                  <MenuItem key={g.value} value={g.value}>
                    {g.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Status"
                value={form.active ? "true" : "false"}
                onChange={(e) => updateField("active", e.target.value === "true")}
                fullWidth
              >
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </TextField>
            </Grid>
          </Grid>
          <Button
            type="submit"
            disabled={isSaving}
            variant="contained"
            style={{
              backgroundColor: "#213360",
              color: "#fff",
              borderRadius: "20px",
              marginTop: "20px",
            }}
          >
            Save Changes
          </Button>
        </form>
      </Box>
    </Modal>
  );
}
